'use client';

import type { MouseEventHandler, ReactNode } from 'react';
import { Fragment } from 'react';
import {
  SummarizerActionControls,
  type SummarizerSecondaryAction,
} from '@/components/summarizer/SummarizerActionControls';
import { SummarizerSummaryCard, type SummarizerSummaryCardProps } from '@/components/summarizer/SummarizerSummaryCard';
import { cn } from '@/lib/utils';

interface PrimaryActionConfig {
  align?: 'start' | 'center' | 'end';
  className?: string;
  disabled?: boolean;
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  secondaryAction?: SummarizerSecondaryAction | null;
  visible?: boolean;
}

type PanelCardProperties = Omit<SummarizerSummaryCardProps, 'leadingActions' | 'trailingActions'> & {
  className?: string;
  leadingActions?: ReactNode;
  trailingActions?: ReactNode;
};

interface SummarizerPanelProperties {
  cardProps: PanelCardProperties;
  className?: string | undefined;
  dataTestId?: string | undefined;
  headerLeadingActions?: ReactNode | undefined;
  headerTrailingActions?: ReactNode | undefined;
  metadataItems?: string[] | undefined;
  primaryAction?: PrimaryActionConfig | undefined;
  showCard?: boolean | undefined;
}

export function SummarizerPanel({
  className,
  dataTestId,
  primaryAction,
  cardProps,
  metadataItems,
  headerLeadingActions,
  headerTrailingActions,
  showCard = true,
}: SummarizerPanelProperties) {
  const shouldRenderPrimary = Boolean(primaryAction) && (primaryAction?.visible ?? true);

  let resolvedPrimaryAlign = 'justify-end';
  if (primaryAction?.align === 'start') {
    resolvedPrimaryAlign = 'justify-start';
  } else if (primaryAction?.align === 'center') {
    resolvedPrimaryAlign = 'justify-center';
  }

  const combinedLeadingActions = combineActions(
    { key: 'header-leading', node: headerLeadingActions },
    { key: 'card-leading', node: cardProps.leadingActions }
  );
  const hasHeaderTrailingActions = headerTrailingActions != null;
  const hasExplicitTrailing = cardProps.trailingActions != null;
  const trailingConfig: TrailingActionOptions = {
    ...(metadataItems ? { metadataItems } : {}),
    ...(hasHeaderTrailingActions ? { headerTrailingActions } : {}),
    ...(hasExplicitTrailing ? { explicitTrailing: cardProps.trailingActions } : {}),
  };
  const combinedTrailingActions = buildTrailingActions(trailingConfig);

  const shouldRenderSurface = shouldRenderPrimary || showCard;
  if (shouldRenderSurface) {
    return (
      <div className={cn('space-y-3', className)} data-testid={dataTestId}>
        {shouldRenderPrimary && primaryAction ? (
          <div className={cn('flex items-center', resolvedPrimaryAlign)}>
            <SummarizerActionControls
              label={primaryAction.label}
              onPrimaryClick={primaryAction.onClick}
              disabled={primaryAction.disabled ?? false}
              {...(primaryAction.secondaryAction === undefined
                ? {}
                : { secondaryAction: primaryAction.secondaryAction })}
              {...(primaryAction.className ? { className: primaryAction.className } : {})}
            />
          </div>
        ) : null}

        {showCard ? (
          <SummarizerSummaryCard
            {...cardProps}
            leadingActions={combinedLeadingActions}
            trailingActions={combinedTrailingActions}
          />
        ) : null}
      </div>
    );
  }

  return null;
}

function combineActions(...actions: { key: string; node?: ReactNode }[]): ReactNode | undefined {
  const filtered: { key: string; node: ReactNode }[] = actions.filter(
    (entry): entry is { key: string; node: ReactNode } => entry.node !== undefined
  );
  if (filtered.length === 0) {
    return;
  }
  if (filtered.length === 1) {
    return filtered[0]?.node;
  }
  return (
    <div className="flex items-center gap-1.5">
      {filtered.map(({ key, node }) => (
        <Fragment key={key}>{node}</Fragment>
      ))}
    </div>
  );
}

type TrailingActionOptions = {
  metadataItems?: string[];
  headerTrailingActions?: ReactNode;
  explicitTrailing?: ReactNode;
};

function buildTrailingActions({
  metadataItems,
  headerTrailingActions,
  explicitTrailing,
}: TrailingActionOptions): ReactNode | undefined {
  const pieces: { key: string; node: ReactNode }[] = [];

  if (metadataItems && metadataItems.length > 0) {
    pieces.push({
      key: 'metadata',
      node: (
        <span className="text-[10px] font-semibold tracking-[0.22em] text-muted-foreground uppercase whitespace-nowrap">
          {metadataItems.join(' • ').toUpperCase()}
        </span>
      ),
    });
  }

  if (headerTrailingActions != null) {
    pieces.push({ key: 'headerTrailing', node: headerTrailingActions });
  }

  if (explicitTrailing != null) {
    pieces.push({ key: 'explicitTrailing', node: explicitTrailing });
  }

  if (pieces.length === 0) {
    return;
  }

  if (pieces.length === 1) {
    return pieces[0]?.node;
  }

  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      {pieces.map(({ key, node }) => (
        <Fragment key={key}>{node}</Fragment>
      ))}
    </div>
  );
}
export type { SummarizerPanelProperties as SummarizerPanelProps };
